/**
 * Types for GET /api/dashboard/stats (plain JSON body on success).
 */

export interface UpcomingSet {
  id: string
  setName: string
  setCode: string
  language?: string | null
  releaseDate: string | null
  logoUrl: string | null
  symbolUrl?: string | null
  /** A picture from the set's own cards, for sets with no publisher logo. */
  coverImageUrl?: string | null
  series: string | null
  cardCount: number | null
  gameName: string
}

/** GET /api/dashboard/today. All money is store-currency cents, in `currency`. */
export interface DashboardTodayFigures {
  date: string
  salesCents: number
  onlineCents: number
  inStoreCents: number
  orders: number
  units: number
  tradeInCents: number
  tradeInCount: number
  lastWeekSoFarCents: number
  days: Array<{ date: string; salesCents: number }>
  topSellers: Array<{ productId: string; name: string; imageUrl: string | null; units: number; salesCents: number }>
}

export interface DashboardToday {
  currency: string
  /** Null when the store's exchange rate is unavailable. */
  figures: DashboardTodayFigures | null
}

export interface TopSellingCard {
  name: string
  imageUrl: string | null
  salesCount: number
  revenue: number
}

export interface PriceChangeRow {
  cardId?: string
  cardName: string
  imageUrl: string | null
  /** Pricing source the change was computed from ('justtcg' | 'scryfall'). Older payloads omit this. */
  source?: string
  oldPrice: number
  newPrice: number
  changePercent: number
  changeDate: string
  variantId?: string | null
  printing?: string | null
  condition?: string | null
}

export interface RecentTradeSummary {
  id: string
  customerName: string | null
  netCashCents: number
  completedAt: string
}

export interface DashboardConcerns {
  draftTradesStaleCount: number
  salesEndingTodayCount: number
  manualPricesExpiring24hCount: number
  instoreWeekNetCashCents: number
}

export interface DashboardDataQuality {
  missingCostCount: number
}

export interface DashboardStatsPayload {
  inventory: {
    totalProducts: number
    recentlyAdded: number
    lowStockCount: number
    /**
     * Raw Σ quantity × USD `priceCents` (no manual price, sale, floor, markup or listing rate).
     * Kept by the API for compatibility; never shown — see {@link listedValue}.
     */
    totalValueCents?: number
    /**
     * Stock on hand at its LISTED prices, in `currency` (store currency), every row priced by the
     * server's listing engine exactly as the inventory screen prices it. Shown verbatim; null
     * (or absent on older APIs) means it could not be computed — show "unavailable".
     */
    listedValue?: { cents: number; currency: string; unpricedProducts: number } | null
  }
  /** SKUs missing unit cost, etc. Emitted when API supports it. */
  dataQuality?: DashboardDataQuality
  sales: {
    todayRevenue: number
    weekRevenue: number
    monthRevenue: number
    topSellingCard: TopSellingCard | null
    /** Top 5 sellers by revenue over the last 7 days. Older payloads only send topSellingCard. */
    topSellingCards?: TopSellingCard[]
  }
  activity: {
    apiCallsToday: number
    lastSync: string | null
    nextPriceSyncAt: string
    recentTrades: number
    /** Completed in-store trades in the last 7 days (excludes draft/cancelled). */
    completedInstoreTradesWeek?: number
    recentTradeSummaries?: RecentTradeSummary[]
  }
  concerns?: DashboardConcerns
  priceChanges: PriceChangeRow[]
  upcomingSets?: UpcomingSet[]
}

export function parseDashboardStatsPayload(raw: unknown): DashboardStatsPayload {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Invalid dashboard stats response')
  }
  const obj = raw as { data?: DashboardStatsPayload }
  return obj.data ?? (raw as DashboardStatsPayload)
}

export function normalizeDashboardStats(s: DashboardStatsPayload): DashboardStatsPayload {
  const concerns: DashboardConcerns = {
    draftTradesStaleCount: 0,
    salesEndingTodayCount: 0,
    manualPricesExpiring24hCount: 0,
    instoreWeekNetCashCents: 0,
    ...s.concerns,
  }
  const dataQuality: DashboardDataQuality = {
    missingCostCount: 0,
    ...s.dataQuality,
  }
  return {
    ...s,
    concerns,
    dataQuality,
    activity: {
      ...s.activity,
      completedInstoreTradesWeek: s.activity.completedInstoreTradesWeek ?? s.activity.recentTrades,
      recentTradeSummaries: s.activity.recentTradeSummaries ?? [],
    },
    priceChanges: (s.priceChanges ?? []).map((row) => ({
      ...row,
      cardId: row.cardId,
    })),
  }
}
